import {
    NumberField,
    TextField,
    usePermissions,
    useRecordContext,
    FunctionField,
    TopToolbar,
    ArrayField,
    SimpleList,
    useTranslate,
    SelectInput,
    useListContext,
} from 'react-admin';
import CustomCreate from '@/components/global/list/CustomCreate.jsx';
import { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import CustomList from '@/components/global/list/CustomList';
import { CustomDatagridConfigurable } from '@/components/global/list/CustomDatagridConfigurable.jsx';
import { RelationLink } from '@/components/global/buttons/ListButtons';
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import CustomShowModalButton from '@/components/global/buttons/CustomShowModalButton.jsx';
import CustomEditModalButton from '@/components/global/buttons/CustomEditModalButton.jsx';
import CustomCreateButton from '@/components/global/buttons/CustomCreateButton.jsx';
import CustomExportButton from '@/components/global/buttons/CustomExportButton.jsx';
import CustomToolbarActionsPopover from '@/components/global/buttons/CustomToolbarActionsPopover.jsx';
import { CustomSelectColumnsButton } from '@/components/global/buttons/CustomSelectColumnsButton.jsx';
import ColumnsIcon from '@/assets/icons/columns-icon.svg?react';
import { PeriodFilter, ReferenceArrayFilter } from '@/components/global/filters/filters.jsx';
import { SearchBar } from '@/components/global/list/searchBar';
import CustomMorePopover from '@/components/global/buttons/CustomMorePopover';
import { formatPhoneNumber } from '@/helpers/format/formatPhoneNumber.js';
import { useDrawer } from '@/composables/useDrawer';
import { RightDrawer } from '@/components/global/drawers/RightDrawer';
import { SearchFilterInput } from '@/components/global/filters/SearchFilterInput';
import DeviceUptimeField from '@/components/device/DeviceUptimeField.jsx';
import { useNavigate } from 'react-router-dom';
import DeviceTransactionsField from '@/components/device/DeviceTransactionsField.jsx';
import { PeriodFilterInput } from '@/components/global/filters/PeriodFilterInput.jsx';
import FromToFilterComponent from '@/components/global/filters/FromToFilterComponent.jsx';
import UnassignedField from '@/components/global/fields/UnassignedField.jsx';
import DeviceStateIndicator from '@/components/device/DeviceStateIndicator.jsx';
import { CollectionPointStatisticList } from '@/components/analytics/statisticList.jsx';
import FilterIcon from '@/assets/icons/filter-icon.svg?react';
import LocationCollectionPointField from '@/components/collectionPoint/LocationCollectionPointField.jsx';
import { CollectionPointForm } from '@/components/collectionPoint/CollectionPointForm.jsx';
import CustomFilterButton from '@/components/global/filters/CustomFilterButton';
import { allOperatorFilterChoices, allCouponsFilterChoices } from '@/components/global/options/selectOptions';
import { styled } from '@mui/material/styles';
import { breakpoints } from '@/themes/themeOverrides/Breakpoints';
import { Palette } from '@/themes/themeOverrides/Palette';
import { DEFAULT_PERIOD } from '@/components/analytics/PeriodFilters.jsx';
import CachedDataActionButton from '@/components/global/buttons/CachedDataActionButton.jsx';

const MoreActionsPopover = ({ permissions }) => {
    const isAdmin = permissions.isAdmin || permissions.isBok;
    const isService = permissions.isService || permissions.isServiceMaster;
    const hasEdit = permissions.isClientMaster || permissions.isServiceMaster || isAdmin;
    const { openDrawer } = useDrawer();
    const translate = useTranslate();

    const record = useRecordContext();
    if (!record) return null;

    return (
        <>
            <CustomMorePopover permissions={permissions} record={record}>
                <CustomShowModalButton
                    onOpen={() => openDrawer(record.id, 'collection-point-show')}
                    label={translate('resources.collectionPoint.labels.showCollectionPoint')}
                />
                {hasEdit && (
                    <CustomEditModalButton
                        onOpen={() => openDrawer(record.id, 'collection-point-edit')}
                        label={translate('resources.collectionPoint.labels.editCollectionPoint')}
                    />
                )}
                {!isService && (
                    <RelationLink
                        label={translate('resources.collectionPoint.labels.viewDevicesFromCollectionPoint')}
                        startIcon={<FilterIcon />}
                        resource='device'
                        filter='collectionPoint->id_in'
                    />
                )}
                {isAdmin && (
                    <RelationLink
                        label={translate('resources.collectionPoint.labels.viewNewDevicesFromCollectionPoint')}
                        startIcon={<FilterIcon />}
                        resource='new-device'
                        filter='collectionPoint->id_in'
                    />
                )}
                {!isService && (
                    <RelationLink
                        label={translate('resources.collectionPoint.labels.viewTransactionsFromCollectionPoint')}
                        startIcon={<FilterIcon />}
                        resource='transaction'
                        filter='collectionPoint->id_in'
                    />
                )}
            </CustomMorePopover>
        </>
    );
};

MoreActionsPopover.propTypes = {
    permissions: PropTypes.shape({
        isService: PropTypes.bool,
        isServiceMaster: PropTypes.bool,
    }).isRequired,
};

export const CollectionPointCreate = ({ handleClose }) => {
    const { isLoading, permissions } = usePermissions();
    const translate = useTranslate();

    useEffect(() => {
        if (!isLoading && !permissions?.isAdmin) {
            handleClose();
        }
    }, [isLoading, permissions, handleClose]);

    if (!isLoading && !permissions?.isAdmin) {
        return null;
    }

    return (
        <RightDrawer handleClose={handleClose} title={translate('resources.collectionPoint.labels.addCollectionPoint')}>
            <CustomCreate handleClose={handleClose}>
                <CollectionPointForm handleClose={handleClose} />
            </CustomCreate>
        </RightDrawer>
    );
};

CollectionPointCreate.propTypes = {
    handleClose: PropTypes.func.isRequired,
};

const StyledCollectionPointListContent = styled(Box)(() => ({
    '&.pos-list': {
        '& .statistics-boxes': {
            [`@media (max-width: ${breakpoints.values.md}px)`]: {
                marginBottom: '20px !important',
            },
        },
        '& .MuiTableRow-root.RaDatagrid-row': {
            [`@media (max-width: ${breakpoints.values.lg}px)`]: {
                padding: '24px 24px 0px 24px',
            },
        },
        [`@media (max-width: ${breakpoints.values.lg}px)`]: {
            '& .RaDatagrid-root.responsive-table .RaDatagrid-row': {
                flexDirection: 'row',
                flexWrap: 'wrap',
                alignItems: 'center',
                columnGap: '16px',
                rowGap: '12px',
            },
            '& .RaDatagrid-root.responsive-table .MuiTableCell-root:not(.pos-metrics-cell):not(.column-analytics\\.uptime):not(.column-analytics\\.transactionsCount)':
                {
                    flexBasis: '100%',
                    width: '100%',
                },
            '& .RaDatagrid-root.responsive-table .column-analytics\\.uptime, & .RaDatagrid-root.responsive-table .column-analytics\\.transactionsCount': {
                flex: '0 0 auto',
                flexBasis: 'auto',
                width: 'auto',
            },
        },
        '& .contact-field, & .column-serviceCompany\\.name, & .options-field, & .column-analytics\\.bagsCount': {
            [`@media (max-width: ${breakpoints.values.lg}px)`]: {
                display: 'none !important',
            },
        },
        '& .pos-field-adress': {
            [`@media (max-width: ${breakpoints.values.lg}px)`]: {
                marginTop: '6px',
            },
        },
        '& .column-devices': {
            [`@media (max-width: ${breakpoints.values.lg}px)`]: {
                margin: '2px 0 -15px 0',
                '& .MuiList-root': {
                    borderTop: `2px solid ${Palette.gray.gray75}`,
                    borderBottom: `2px solid ${Palette.gray.gray75}`,
                    paddingTop: '10px',
                    paddingBottom: '10px',
                    '& .MuiListItemText-multiline': {
                        '& .MuiListItemText-primary .MuiTypography-caption': {
                            fontSize: '11px',
                            lineHeight: '16px',
                            letterSpacing: '-0.11px',
                            marginBottom: '2px',
                        },
                    },
                },
                '& .unassigned-field': {
                    fontSize: '11px',
                    lineHeight: '16px',
                    letterSpacing: '-0.11px',
                },
            },
        },
    },
}));

const StyledCollectionPointList = ({ children, ...props }) => (
    <CustomList {...props}>
        <StyledCollectionPointListContent className='responsive-table pos-list'>{children}</StyledCollectionPointListContent>
    </CustomList>
);

StyledCollectionPointList.propTypes = {
    children: PropTypes.node,
};

const ListActions = () => {
    const { isLoading, permissions } = usePermissions();
    const translate = useTranslate();
    if (isLoading) return null;

    return (
        <TopToolbar>
            <SearchFilterInput />
            <PeriodFilterInput />
            <CustomFilterButton />
            <CustomSelectColumnsButton startIcon={<ColumnsIcon />} />
            {(permissions.isAdmin || permissions.isBok || permissions.isServiceMaster) && (
                <CustomToolbarActionsPopover>
                    {permissions?.isAdmin && <CustomCreateButton label={translate('resources.collectionPoint.labels.createCollectionPoint')} />}
                    <CustomExportButton label={translate('common.exportCsv')} />
                    {permissions.isAdmin && <CachedDataActionButton resource='collection-point' type={null} />}
                </CustomToolbarActionsPopover>
            )}
        </TopToolbar>
    );
};

const TransactionsClickable = ({ record }) => {
    const navigate = useNavigate();
    const handleClick = e => {
        e.stopPropagation();
        const filter = encodeURIComponent(JSON.stringify({ 'collectionPoint->id_in': [record.id] }));
        navigate(`/transaction?filter=${filter}`);
    };
    return (
        <Box sx={{ cursor: 'pointer' }}>
            <DeviceTransactionsField record={record} onClick={handleClick} />
        </Box>
    );
};

const BagsClickable = ({ record }) => {
    const navigate = useNavigate();
    const { filterValues } = useListContext();
    const currentPeriod = filterValues?.period ?? DEFAULT_PERIOD;
    const source = `analytics[${currentPeriod}].bagsCount`;
    const handleClick = e => {
        e.stopPropagation();
        const filter = encodeURIComponent(JSON.stringify({ 'collectionPoint->id_in': [record.id] }));
        navigate(`/bag?filter=${filter}`);
    };
    return (
        <Box sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }} onClick={handleClick}>
            <NumberField source={source} record={record} emptyText='0' />
        </Box>
    );
};

export const CollectionPointList = () => {
    const { isLoading, permissions } = usePermissions();
    const { openDrawer } = useDrawer();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const translate = useTranslate();

    const isAdminOrBok = permissions.isAdmin || permissions.isBok;

    const filters = useMemo(
        () => [
            <SearchBar key='search' source='value' label={translate('common.search')} alwaysOn />,
            <PeriodFilter key='period' alwaysOn={true} className='period-filter' forceDesktopOnMobile={true} />,
            isAdminOrBok && (
                <ReferenceArrayFilter
                    key='contractor'
                    source='contractorId_in'
                    reference='contractor'
                    label={translate('common.contractor')}
                    forceDesktopOnMobile={true}
                    alwaysOn={isMobile}
                />
            ),
            <ReferenceArrayFilter
                key='city'
                source='address->city_in'
                reference='city'
                label={translate('common.city')}
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
            isAdminOrBok && (
                <SelectInput
                    key='operator'
                    source='operator'
                    label={translate('common.operator')}
                    choices={allOperatorFilterChoices(translate)}
                    emptyText={translate('common.all')}
                    alwaysOn={isMobile}
                />
            ),
            isAdminOrBok && (
                <SelectInput
                    key='couponSource'
                    source='couponSource'
                    label={translate('resources.collectionPoint.fields.couponSource')}
                    choices={allCouponsFilterChoices(translate)}
                    emptyText={translate('common.all')}
                    alwaysOn={isMobile}
                />
            ),
            <FromToFilterComponent
                key='transactionCount'
                source='analytics->transactionsCount_range'
                label={translate('common.transactionsNumber')}
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
            <FromToFilterComponent
                key='containerCount'
                source='analytics->bagsCount_range'
                label={translate('resources.collectionPoint.filters.bagsCount')}
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
            <FromToFilterComponent
                key='uptime'
                source='analytics->uptime_range'
                label={translate('resources.collectionPoint.filters.uptime')}
                type='percent'
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
            <FromToFilterComponent
                key='updatedAt'
                source='updatedAt_range'
                label={translate('common.updatedAtFilter')}
                type='date'
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
        ],
        [isMobile, isAdminOrBok, translate],
    ).filter(Boolean);

    if (isLoading) return null;

    return (
        <StyledCollectionPointList actions={<ListActions />} filters={filters} addLastColumnShadow={true}>
            {!permissions.isService && <CollectionPointStatisticList />}
            <CustomDatagridConfigurable
                bulkActionButtons={false}
                className='responsive-table'
                rowClick={id => {
                    openDrawer(id, 'collection-point-show');
                    return false;
                }}
            >
                <FunctionField
                    label={translate('common.collectionPoint')}
                    sortBy='customId'
                    render={record => <LocationCollectionPointField collectionPoint={record} />}
                />
                <ArrayField source='devices' label={translate('menu.devices')} sortBy='deviceCount'>
                    <SimpleList
                        primaryText={record => {
                            return (
                                <Box
                                    onClick={e => {
                                        e.stopPropagation();
                                        openDrawer(record.id, 'device-show');
                                    }}
                                    sx={{
                                        display: 'flex',
                                        gap: '4px',
                                        cursor: 'pointer',
                                        [`@media (max-width: ${breakpoints.values.md}px)`]: {
                                            flexWrap: 'wrap',
                                        },
                                        '& .MuiTypography-caption': {
                                            whiteSpace: 'nowrap',
                                        },
                                        '&:hover': {
                                            textDecoration: 'underline !important',
                                        },
                                    }}
                                >
                                    <Typography variant='caption'>{record.serialNumber},</Typography>
                                    <Typography variant='caption'>{record.group?.name ?? ''}</Typography>
                                </Box>
                            );
                        }}
                        secondaryText={record => <DeviceStateIndicator record={record} />}
                        linkType={false}
                        empty={<UnassignedField sx={{ fontWeight: 400 }} />}
                    />
                </ArrayField>
                <FunctionField
                    label={translate('common.contact')}
                    className='contact-field'
                    render={record => (
                        <Box>
                            <Typography sx={{ fontWeight: '600', letterSpacing: '-0.14px' }} variant='body1'>
                                {record.contactName}
                            </Typography>
                            <Box sx={{ fontSize: '11px', lineHeight: '16px', letterSpacing: '-0.11px', whiteSpace: 'nowrap' }}>
                                {record.contactEmail}
                                {record.contactPhoneNumber && `, ${formatPhoneNumber(record.contactPhoneNumber)}`}
                                {record.employeePhoneNumber && (
                                    <>
                                        ,<br />
                                        {formatPhoneNumber(record.employeePhoneNumber)} (
                                        {translate('resources.collectionPoint.labels.employeePhoneNumberLabel')})
                                    </>
                                )}
                            </Box>
                        </Box>
                    )}
                />
                <FunctionField
                    cellClassName='pos-metrics-cell'
                    source='analytics.uptime'
                    label={translate('common.uptime')}
                    render={record => <DeviceUptimeField record={record} />}
                />
                <FunctionField
                    cellClassName='pos-metrics-cell'
                    source='analytics.transactionsCount'
                    label={translate('common.transactionsCount')}
                    render={record => <TransactionsClickable record={record} />}
                />
                <FunctionField source='analytics.bagsCount' label={translate('common.bagsCount')} render={record => <BagsClickable record={record} />} />
                <TextField source='operator' label={translate('common.operator')} />
                <FunctionField
                    source='serviceCompany.name'
                    label={translate('resources.user.fields.serviceCompanyId')}
                    render={record => (
                        <TextField
                            source='serviceCompany.name'
                            label={translate('resources.user.fields.serviceCompanyId')}
                            onClick={e => {
                                e.stopPropagation();
                                openDrawer(record.serviceCompanyId, 'service-company-show');
                            }}
                            sx={{ '&:hover': { textDecoration: 'underline' } }}
                        />
                    )}
                />
                <FunctionField
                    className='options-field'
                    label={translate('common.options')}
                    onClick={e => e.stopPropagation()}
                    render={record => <MoreActionsPopover permissions={permissions} groupId={record.id} />}
                />
            </CustomDatagridConfigurable>
        </StyledCollectionPointList>
    );
};

CollectionPointCreate.propTypes = {
    handleClose: PropTypes.func,
};
